// pages/alumni/index.tsx
import { AlumniActionType, createAlumniColumns } from "@/colDef/alumniColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import AlumniDialog from "@/components/formDialog/AlumniDialog";
import Search from "@/components/Search";
import {
  useAlumniQuery,
  useDeleteAlumniMutation,
} from "@/hooks/api/alumni.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import { AlumniParamsType, AlumniType } from "@/services/alumni.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const AlumniPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const alumniDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    alumniId: "",
    alumniData: null as AlumniType | null,
  });

  const filters: AlumniParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
    }),
    [debouncedSearch, page, limit]
  );

  const { data, isLoading, isError, refetch } = useAlumniQuery(filters);
  const deleteMutation = useDeleteAlumniMutation(filters);

  const alumni = data?.alumni ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: AlumniActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to remove{" "}
              <strong className="text-gray-800">{payload.name}</strong> from
              alumni records? This action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const alumniData = alumni.find((a) => a._id === payload.id) ?? null;
        alumniDialog.open({ mode: "edit", alumniId: payload.id, alumniData });
      }
    },
    [deleteDialog, alumniDialog, alumni]
  );

  const columns = useMemo(
    () => createAlumniColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load alumni records."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Alumni Management</h1>
        <div className="flex items-center gap-3">
          {/* Advanced Filters — placeholder for future implementation */}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => {
              /* TODO: open advanced filters panel */
            }}
          >
            <Filter size={15} />
            Advanced Filters
          </Button>

          <Button
            onClick={() =>
              alumniDialog.open({
                mode: "create",
                alumniId: "",
                alumniData: null,
              })
            }
            className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
          >
            <Plus size={16} />
            Add New
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<AlumniType, any> columns={columns} data={alumni} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={alumni.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog */}
      <AlumniDialog
        mode={alumniDialog.state.mode}
        isOpen={alumniDialog.state.isOpen}
        onOpenChange={(open) => !open && alumniDialog.close()}
        filters={filters}
        alumniId={alumniDialog.state.alumniId}
        alumniData={alumniDialog.state.alumniData}
        onCancel={alumniDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default AlumniPage;
